import { isTauriRuntime } from "@/lib/desktop-shell";
import { sanitizeAnalyticsPagePath } from "@/lib/analytics-path";

export { sanitizeAnalyticsPagePath } from "@/lib/analytics-path";

/** Build-time override; defaults to production GA4 property when unset. */
export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID?.trim() || "G-1NXL8NVZ0L";

const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/;

type GtagCommand = "js" | "config" | "event";
type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: GtagFn;
  }
}

let initialized = false;

function analyticsEnabled(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  if (isTauriRuntime()) {
    return false;
  }
  if (!GA_MEASUREMENT_ID || !GA4_ID_PATTERN.test(GA_MEASUREMENT_ID)) {
    return false;
  }
  return true;
}

/** Inject gtag.js only after the page has loaded and the main thread is idle. */
function scheduleGtagScript(): void {
  const inject = (): void => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  };
  const whenIdle = (): void => {
    if (typeof window.requestIdleCallback === "function") {
      window.requestIdleCallback(inject, { timeout: 4000 });
    } else {
      window.setTimeout(inject, 1500);
    }
  };
  if (document.readyState === "complete") {
    whenIdle();
  } else {
    window.addEventListener("load", whenIdle, { once: true });
  }
}

function ensureGtagLoaded(): GtagFn | null {
  if (!analyticsEnabled()) {
    return null;
  }
  if (window.gtag) {
    return window.gtag;
  }

  window.dataLayer = window.dataLayer ?? [];
  const gtag: GtagFn = (...args) => {
    window.dataLayer!.push(args);
  };
  window.gtag = gtag;

  gtag("js", new Date());

  scheduleGtagScript();

  return gtag;
}

/** Load GA4 once; manual page_view events only (no automatic enhanced measurement). */
export function initGoogleAnalytics(): void {
  if (initialized || !analyticsEnabled()) {
    return;
  }
  const gtag = ensureGtagLoaded();
  if (!gtag) {
    return;
  }

  gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });

  initialized = true;
}

/** Fire a single page_view with sanitized path/location (never query, hash, or tokens). */
export function trackAnalyticsPageView(pathname: string): void {
  if (!analyticsEnabled()) {
    return;
  }
  initGoogleAnalytics();
  const gtag = window.gtag;
  if (!gtag) {
    return;
  }

  const pagePath = sanitizeAnalyticsPagePath(pathname);
  const pageLocation = `${window.location.origin}${pagePath}`;

  gtag("event", "page_view", {
    page_path: pagePath,
    page_location: pageLocation,
  });
}
